import type { EmitFn } from "vue";
import { useStore } from "vuex";

import type { CSSSettings, EditableValue, Editable, MasterTemplate } from "hoxton-types";
import nl2br from "hoxton-shared/utils/nl2br";
import { EditableType } from "hoxton-shared/enums/editables";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";
import { OverwriteScope } from "hoxton-shared/enums/overwrites";

import { useMasterTemplates } from "@/composables/useMasterTemplates";
import { useOverwrite } from "@/composables/useOverwrite";
import { useEditor } from "@/composables/useEditor";

import type { RootState } from "@/store/types";
import { EditorGetters } from "@/store/modules/editor";

import { EditableEvent, EditableScope } from "@/enums/editables";
import type { MediaItem, EditableOverwrite } from "@/types/types";
import * as editableUtils from "@/utils/editables";

function computeEditableSettings(
    editable: Editable,
    masterTemplateId: string,
    groupOverwrite: EditableOverwrite,
    templateOverwrite: EditableOverwrite
) {
    const defaultSettings = editableUtils.getDefaultSettings(editable, masterTemplateId);
    const settingsFromGroup = editableUtils.getEditableSettings(groupOverwrite);
    const settingsFromTemplate = editableUtils.getEditableSettings(templateOverwrite);

    const result = { ...defaultSettings, ...settingsFromGroup, ...settingsFromTemplate } as Record<string, unknown>;

    const templateSettingsAsMap = settingsFromTemplate as Record<string, unknown> | null | undefined;
    if (
        templateSettingsAsMap != null &&
        "fontFamily" in templateSettingsAsMap &&
        templateSettingsAsMap.fontFamily === null
    ) {
        delete result.fontFamily;
    }

    return result;
}

export function useEditableMethods(params?: { emit?: EmitFn }) {
    const emit = params?.emit;
    const store = useStore<RootState>();

    const { masterTemplates } = useMasterTemplates();
    const { selectedGroupValues, editables } = useEditor();
    const { getOverwrite, getOverwriteMediaItem } = useOverwrite();

    function getAffectingGroups(editable: Editable, masterTemplateId: string) {
        if (masterTemplateId) {
            const storeEditable = editables.value[editable._id];

            const groups = storeEditable!.defaultValues.filter(def => def.masterTemplateId === masterTemplateId);
            const editableGroups = groups[0]?.editableGroups;

            if (editableGroups?.length) {
                return editableGroups;
            }

            return [];
        }

        const map = (store.getters as Record<string, unknown>)[EditorGetters.groupsForSelectedEditable] as Record<
            string,
            string[]
        >;

        if (editable._id in map) {
            return map[editable._id];
        }

        return [];
    }

    function getAffectingGroupValueIds(groupsAffectingEditable: string[]) {
        return groupsAffectingEditable.reduce<string[]>((acc, groupName) => {
            if (groupName in selectedGroupValues.value) {
                const group = selectedGroupValues.value[groupName];
                acc.push(group!.groupValueId);
            }

            return acc;
        }, []);
    }

    function getEditableValue(editable: Editable, groupsAffectingEditable: string[], masterTemplateId: string) {
        const overwriteValueObject = getOverwrite(editable, groupsAffectingEditable, masterTemplateId);

        const computedEditableValue = editableUtils.computeEditableValue(
            editable,
            masterTemplateId,
            overwriteValueObject,
            masterTemplates.value
        );

        return computedEditableValue ?? "";
    }

    function getEditablePreviewValue(
        editable: Editable,
        groupsAffectingEditable: string[],
        { _id, adType }: MasterTemplate
    ) {
        const value = getEditableValue(editable, groupsAffectingEditable, _id);

        if (editable.type === EditableType.Folder) {
            const mediaItem = getOverwriteMediaItem(editable, groupsAffectingEditable, _id);
            const newValue = `${(value as string).replace(/^\/|\/$/g, "")}/`;

            if (mediaItem) {
                return `/${newValue}`;
            }

            return newValue;
        }

        if (editable.type === EditableType.Textarea && adType !== TemplateType.PSD) {
            return nl2br(value as string);
        }

        return value;
    }

    function getGroupScope(editable: Editable) {
        // TODO decide if we should remove all the campaign and template level overwrite references & endpoints
        return !editable.scope || editable.scope === EditableScope.Group
            ? OverwriteScope.EditableGroup
            : OverwriteScope.Campaign;
    }

    function getTemplateScope(editable: Editable) {
        return !editable.scope || editable.scope === EditableScope.Group
            ? OverwriteScope.EditableGroupTemplate
            : OverwriteScope.Template;
    }

    function getForKey(editable: Editable, templateId: string) {
        return `${editable._id}_${templateId}`;
    }

    function getLabelColor(editable: Editable, groupsAffectingEditable: string[], templateId: string) {
        const overwrite = getOverwrite(editable, groupsAffectingEditable, templateId);

        return overwrite.value || overwrite.value === "" || overwrite.mediaItem ? "primary" : "default";
    }

    function update({
        editable,
        updateData,
        groupsAffectingEditable,
        templateId = null
    }: {
        editable: Editable;
        updateData: { value: EditableValue; mediaItem?: MediaItem };
        groupsAffectingEditable: string[];
        templateId?: string | null;
    }) {
        const updatePayload: {
            scope: ReturnType<typeof getGroupScope> | ReturnType<typeof getTemplateScope>;
            editable: Editable;
            groupsAffectingEditable: string[];
            masterTemplateId?: string | null;
            value?: EditableValue;
            mediaItem?: MediaItem;
        } = {
            scope: getGroupScope(editable),
            editable,
            groupsAffectingEditable,
            ...updateData
        };

        if (templateId) {
            updatePayload.scope = getTemplateScope(editable);
            updatePayload.masterTemplateId = templateId;
        }

        emit?.(EditableEvent.Update, updatePayload);
    }

    function restore({
        editable,
        templateId,
        language
    }: {
        editable: Editable;
        templateId: string | null;
        language: string;
    }) {
        const updatePayload: {
            editable: Editable;
            language: string;
            masterTemplateId?: string | null;
            scope: ReturnType<typeof getGroupScope> | ReturnType<typeof getTemplateScope>;
        } = {
            scope: getGroupScope(editable),
            editable,
            language
        };

        if (templateId) {
            updatePayload.scope = getTemplateScope(editable);
            updatePayload.masterTemplateId = templateId;
        }

        emit?.(EditableEvent.Remove, updatePayload);
    }

    function getEditableSettings(editable: Editable, groupsAffectingEditable: string[], masterTemplateId: string) {
        const groupOverwrite = getOverwrite(editable, groupsAffectingEditable, null);
        const templateOverwrite = getOverwrite(editable, groupsAffectingEditable, masterTemplateId);

        return computeEditableSettings(editable, masterTemplateId, groupOverwrite, templateOverwrite);
    }

    function updateSettings({
        editable,
        settings,
        groupsAffectingEditable,
        templateId = null
    }: {
        editable: Editable;
        settings: CSSSettings | null;
        groupsAffectingEditable: string[];
        templateId?: string | null;
    }) {
        const updatePayload: {
            scope: ReturnType<typeof getGroupScope> | ReturnType<typeof getTemplateScope>;
            editable: Editable;
            settings: CSSSettings | null;
            groupsAffectingEditable: string[];
            masterTemplateId?: string | null;
        } = {
            scope: getGroupScope(editable),
            editable,
            settings,
            groupsAffectingEditable
        };

        if (templateId) {
            updatePayload.scope = getTemplateScope(editable);
            updatePayload.masterTemplateId = templateId;
        }

        emit?.(EditableEvent.Update, updatePayload);
    }

    return {
        getAffectingGroups,
        getAffectingGroupValueIds,
        getEditableValue,
        getOverwriteMediaItem,
        getEditablePreviewValue,
        getForKey,
        getLabelColor,
        update,
        restore,
        getGroupScope,
        getTemplateScope,
        getEditableSettings,
        updateSettings,
        computeEditableSettings
    };
}
